import React, { useState, useEffect, useRef, useCallback } from 'react';
import { CircularProgress } from '@mui/material';
import { ClipboardList, CircleCheck, CircleX } from 'lucide-react';
import { buildStyles, CircularProgressbar } from 'react-circular-progressbar';
import 'react-circular-progressbar/dist/styles.css';
import jsPDF from 'jspdf';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import host from '../../global';
import { toast } from 'react-toastify';
import { useTheme } from '../../hooks/useTheme';
import { formatReportDate } from '../../utils/date';

const getScorePathColor = (val) => {
  const n = parseFloat(val) || 0;
  if (n >= 60) return '#34B53A';
  if (n >= 30) return '#FFB200';
  return '#DC0000';
};

const getScoreTrailColor = (val) => {
  const n = parseFloat(val) || 0;
  if (n >= 60) return 'rgba(52,181,58,0.15)';
  if (n >= 30) return 'rgba(255,178,0,0.15)';
  return 'rgba(220,0,0,0.15)';
};

// Accepts an int of seconds (current shape) or a legacy "M:SS" string, and refuses

const formatDuration = (value) => {
  if (value === null || value === undefined || value === '') return '—';
  let seconds;
  if (typeof value === 'number') {
    seconds = value;
  } else {
    const parts = String(value).split(':');
    seconds = parts.length === 2
      ? parseInt(parts[0], 10) * 60 + parseInt(parts[1], 10)
      : parseInt(value, 10);
  }
  if (!Number.isFinite(seconds) || seconds < 0) return '—';
  const h = Math.floor(seconds / 3600);
  const m = String(Math.floor((seconds % 3600) / 60)).padStart(2, '0');
  const s = String(seconds % 60).padStart(2, '0');
  return h > 0 ? `${h}:${m}:${s}` : `${m}:${s}`;
};

const GaugeTile = ({ value, text, label, caption, pathColor, trailColor, textColor }) => (
  <div className="rounded-xl border border-[#52525280] px-5 py-5 flex flex-col items-center text-center gap-2.5">
    <div className="text-sm font-medium text-gray-600 dark:text-gray-300">{label}</div>
    <div style={{ width: 96, height: 96 }}>
      <CircularProgressbar
        value={value}
        text={text}
        styles={buildStyles({
          pathColor,
          trailColor,
          textColor,
          textSize: '22px',
          fontWeight: '600',
        })}
      />
    </div>
    {caption && <div className="text-xs text-gray-400 dark:text-gray-500">{caption}</div>}
  </div>
);

// The score comes from /get_mcqs_test_result rather than a prop, so this report and
// the number it renders share one source.
export default function MCQTestReport({ name, interviewId = null, data = undefined }) {
  const { interview_id: routeInterviewId } = useParams();
  const { theme: isDarkMode } = useTheme();
  // Public share-token route: the parent passes the real id (the route param is the
  // token) and pre-fetched `data` (the authed /get_mcqs_test_result is unavailable).
  const interview_id = interviewId || routeInterviewId;
  const [mcqTestResultData, setMCQTestResultData] = useState(data || {});
  const [loading, setLoading] = useState(!data);
  const reportRef = useRef();
  const accessToken = sessionStorage.getItem("accessToken")

  useEffect(() => {
    if (data) {
      setMCQTestResultData(data);
      setLoading(false);
      return;
    }
    const displayMCQTestResult = async () => {
      try {
        const response = await axios.get(`${host}/get_mcqs_test_result`,{
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
          params: {
            interview_id: interview_id,
          },
        });
        setMCQTestResultData(response.data);
        setLoading(false);
      } catch (error) {
        if (error.response) {
          const status = error.response.status;
          const detail = error.response.data.detail;
          if (status === 401) {
              await new Promise((resolve) => {
                sessionStorage.removeItem('accessToken');
                resolve();
              });
              toast.error(`Unauthorized: ${detail}`);
          } else if (status === 404) {
              toast.warning(`Not Found: ${detail}`);
          } else {
              toast.error(`Error: ${detail}`);
          }
      } else {
          toast.error('An unexpected error occurred. Please try again.');
      }
        setLoading(false);
      }
    };
    displayMCQTestResult();
  }, [interview_id,accessToken,data]);

  const handleDownloadPDF = useCallback(async () => {
    try {
      // Calculate formatted percent
      const calculatedPercent = typeof mcqTestResultData.mcq_percent === 'number'
        ? mcqTestResultData.mcq_percent.toFixed(2)
        : (parseFloat(mcqTestResultData.mcq_percent) || 0).toFixed(2);

      // Convert time_taken to seconds if it's in "MM:SS" format
      const timeTakenSeconds = mcqTestResultData.time_taken
        ? parseInt(mcqTestResultData.time_taken.split(':')[0]) * 60 + parseInt(mcqTestResultData.time_taken.split(':')[1])
        : 0;

      // Create PDF document
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4',
        compress: true,
      });

      // Define PDF dimensions
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 10;
      const contentWidth = pageWidth - 2 * margin;

      // Start position
      let currentY = margin;
      let pageCount = 1;

      // Add header to first page
      pdf.setFontSize(16);
      pdf.setTextColor(0, 0, 0);
      pdf.text(`MCQ Test Report - ${name}`, margin, currentY);
      currentY += 10;

      // Add some basic info
      pdf.setFontSize(10);
      pdf.text(`Date: ${mcqTestResultData.startDateTime || new Date().toLocaleDateString()}`, margin, currentY);
      currentY += 5;
      pdf.text(`Time Taken: ${timeTakenSeconds || 0}s`, margin, currentY);
      currentY += 5;
      pdf.text(`Score: ${calculatedPercent}%`, margin, currentY);
      currentY += 10;

      pdf.setFont('Helvetica', 'normal');

      // Helper function to add a new page
      const addNewPage = () => {
        pdf.addPage();
        pageCount++;
        currentY = margin;

        // Add header to new page
        pdf.setFontSize(10);
        pdf.text(`MCQ Test Report - ${name} (Page ${pageCount})`, margin, currentY);
        currentY += 10;
      };

      // Process MCQ Test Details
      pdf.setFontSize(14);
      pdf.text('MCQ Test Details', margin, currentY);
      currentY += 10;

      // Draw a divider line
      pdf.setDrawColor(200, 200, 200);
      pdf.line(margin, currentY, pageWidth - margin, currentY);
      currentY += 5;

      // Process each question
      if (mcqTestResultData.mcqs && Array.isArray(Object.values(mcqTestResultData.mcqs))) {
        Object.values(mcqTestResultData.mcqs).forEach((question, index) => {
          // Check if we need a new page
          if (currentY > pageHeight - 70) {
            addNewPage();
          }

          // Add question title
          pdf.setFontSize(12);
          pdf.setTextColor(0, 0, 100);
          const splitQuestion = pdf.splitTextToSize(`Question ${index + 1}: ${question.text}`, contentWidth);
          if (currentY + splitQuestion.length * 5 > pageHeight - 30) {
            addNewPage();
          }
          pdf.text(splitQuestion, margin, currentY);
          currentY += splitQuestion.length * 5 + 2;

          // Add options — a list of any length, resolved by option id
          pdf.setFontSize(10);
          pdf.setTextColor(0, 0, 0);
          const options = question.options || [];
          const correctOption = options.find((o) => o.id === question.correct_option_id);
          const givenOption = options.find((o) => o.id === question.selected_option_id);
          options.forEach((option, optIndex) => {
            const splitOption = pdf.splitTextToSize(`${String.fromCharCode(65 + optIndex)}. ${option.text}`, contentWidth - 10);
            if (currentY + splitOption.length * 5 > pageHeight - 30) {
              addNewPage();
            }
            pdf.text(splitOption, margin + 5, currentY);
            currentY += splitOption.length * 5 + 2;
          });

          // Add correct answer
          if (correctOption) {
            const splitCorrect = pdf.splitTextToSize(`Correct Answer: ${correctOption.text}`, contentWidth - 10);
            if (currentY + splitCorrect.length * 5 > pageHeight - 30) {
              addNewPage();
            }
            pdf.text(splitCorrect, margin + 5, currentY);
            currentY += splitCorrect.length * 5 + 2;
          }

          // Add user answer
          if (givenOption) {
            const splitUser = pdf.splitTextToSize(`Your Answer: ${givenOption.text}`, contentWidth - 10);
            pdf.setFont('Helvetica', 'bold');
            if(question.score === 1) {
              pdf.setTextColor(0, 128, 0);
            } else {
              pdf.setTextColor(255, 0, 0);
            }
            if (currentY + splitUser.length * 5 > pageHeight - 30) {
              addNewPage();
            }
            pdf.text(splitUser, margin + 5, currentY);
            pdf.setTextColor(0, 0, 0);
            pdf.setFont('Helvetica', 'normal');
            currentY += splitUser.length * 5 + 2;
          }

          // Add score (or "Not Attempted" in red and bold if no given_answer)
          if (question.score !== undefined && question.score !== null) {
            pdf.text(`Score: ${question.score || 0}`, margin + 5, currentY);
            currentY += 5;
          } else if (!givenOption) {
            if (currentY > pageHeight - 30) {
              addNewPage();
            }
            pdf.setTextColor(255, 0, 0); // Red color
            pdf.setFont('Helvetica', 'bold'); // Bold font
            pdf.text('Not Attempted', margin + 5, currentY);
            pdf.setTextColor(0, 0, 0); // Reset to black
            pdf.setFont('Helvetica', 'normal'); // Reset to normal font
            currentY += 10;
          }

          // Add space between questions
          currentY += 10;

          // Add a separator line between questions
          if (index < Object.values(mcqTestResultData.mcqs).length - 1) {
            pdf.setDrawColor(200, 200, 200);
            pdf.line(margin, currentY - 5, pageWidth - margin, currentY - 5);
          }
        });
      }

      // Add page numbers to footer of each page
      const totalPages = pdf.internal.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        pdf.setPage(i);
        pdf.setFontSize(10);
        pdf.setTextColor(100, 100, 100);
        pdf.text(`Page ${i} of ${totalPages}`, pageWidth - 25, pageHeight - 10);
      }

      // Save the PDF
      pdf.save(`${name}_MCQ_Test_Report.pdf`);
      toast.success('PDF successfully generated!');
    } catch (error) {
      console.error('PDF generation error:', error);
      toast.error(`Error generating PDF: ${error.message || 'Unknown error'}`);
    }
  }, [name, mcqTestResultData, accessToken]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-48">
        <CircularProgress size={32} sx={{ color: '#C9A3FF' }} />
      </div>
    );
  }

  const questions = mcqTestResultData.mcqs ? Object.values(mcqTestResultData.mcqs) : [];
  const totalQuestions = questions.length;
  const attemptedCount = questions.filter(
    (q) => q.selected_option_id !== undefined && q.selected_option_id !== null
  ).length;
  const correctCount = questions.filter(
    (q) => q.selected_option_id != null && q.selected_option_id === q.correct_option_id
  ).length;
  const incorrectCount = attemptedCount - correctCount;
  const skippedCount = totalQuestions - attemptedCount;
  const accuracy = attemptedCount > 0 ? Math.round((correctCount / attemptedCount) * 100) : 0;

  // The percent stored at submit time is authoritative; deriving it only covers
  // reports written before the backend persisted the field (or a stray "NA").
  const storedPercent = parseFloat(mcqTestResultData.mcq_percent);
  const scoreVal = Number.isFinite(storedPercent)
    ? storedPercent
    : totalQuestions > 0
    ? (correctCount / totalQuestions) * 100
    : 0;

  const pct = (n) => (totalQuestions > 0 ? (n / totalQuestions) * 100 : 0);

  // null mcq_time_limit means the job has no MCQ time limit configured — the ratio and
  // pace caption fall through to null and Time Taken degrades to a bare duration.
  const timeLimitSeconds = mcqTestResultData.mcq_time_limit != null
    ? mcqTestResultData.mcq_time_limit * 60
    : null;
  const timeRatio = timeLimitSeconds && mcqTestResultData.time_taken != null
    ? Math.min(mcqTestResultData.time_taken / timeLimitSeconds, 1)
    : null;
  const paceCaption =
    timeRatio == null ? null :
    timeRatio < 0.5 ? 'Finished quickly' :
    timeRatio < 0.9 ? 'Good pace' :
    'Used nearly all the time';
  const paceCaptionClass =
    timeRatio == null ? '' :
    timeRatio < 0.5 ? 'text-amber-500 dark:text-amber-400' :
    timeRatio < 0.9 ? 'text-gray-400 dark:text-gray-500' :
    'text-red-500 dark:text-red-400';

  return (
    <div className="space-y-4 pb-6" ref={reportRef}>

      {/* ── Overview Stats ── */}
      <div className="flex flex-col lg:flex-row items-center lg:items-stretch gap-6 lg:gap-8 px-1 py-2">
        <div className="flex flex-col items-center justify-center flex-shrink-0">
          <div className="relative" style={{ width: 160, height: 160 }}>
            <CircularProgressbar
              value={scoreVal}
              text={`${Math.floor(scoreVal)}%`}
              circleRatio={0.75}
              styles={buildStyles({
                rotation: 1 / 2 + 1 / 8,
                strokeLinecap: 'butt',
                pathColor: getScorePathColor(scoreVal),
                trailColor: getScoreTrailColor(scoreVal),
                textColor: isDarkMode === 'dark' ? '#FFFFFF' : '#1F2937',
                textSize: '24px',
                fontWeight: '600',
              })}
            />
            <div className="absolute bottom-2 left-0 right-0 text-center text-xs text-gray-500 dark:text-gray-400 font-medium">
              Score
            </div>
          </div>
        </div>

        <div className="hidden lg:block w-px self-stretch bg-[#52525280]" />

        <div className="flex-1 flex flex-col justify-center gap-5 min-w-0 w-full">
          {/* Correct is deliberately absent here — the breakdown legend below already
              carries that count, and in proportion against Incorrect and Skipped. */}
          <div className="grid grid-cols-3 gap-4">
            <GaugeTile
              value={pct(attemptedCount)}
              text={`${attemptedCount}/${totalQuestions}`}
              label="Attempted"
              caption={skippedCount > 0 ? `${skippedCount} left blank` : 'All answered'}
              pathColor={isDarkMode === 'dark' ? '#C9A3FF' : '#9333EA'}
              trailColor={isDarkMode === 'dark' ? 'rgba(201,163,255,0.15)' : 'rgba(147,51,234,0.15)'}
              textColor={isDarkMode === 'dark' ? '#FFFFFF' : '#1F2937'}
            />
            <GaugeTile
              value={accuracy}
              text={`${accuracy}%`}
              label="Accuracy"
              caption={attemptedCount > 0 ? `${correctCount} of ${attemptedCount} correct` : 'Not attempted'}
              pathColor={getScorePathColor(accuracy)}
              trailColor={getScoreTrailColor(accuracy)}
              textColor={isDarkMode === 'dark' ? '#FFFFFF' : '#1F2937'}
            />
            <div className="rounded-xl border border-[#52525280] px-5 py-5 flex flex-col items-center justify-center text-center gap-2">
              <div className="text-sm font-medium text-gray-600 dark:text-gray-300">Time Taken</div>
              <div className="leading-tight">
                <span className="text-3xl font-medium text-gray-700 dark:text-gray-300">
                  {formatDuration(mcqTestResultData.time_taken)}
                </span>
                {timeLimitSeconds != null && (
                  <span className="text-base text-gray-400 dark:text-gray-500">
                    {' '}/ {formatDuration(timeLimitSeconds)}
                  </span>
                )}
              </div>
              {paceCaption && (
                <div className={`text-xs font-medium ${paceCaptionClass}`}>{paceCaption}</div>
              )}
            </div>
          </div>

          <div className="flex flex-col gap-2 min-w-0">
            <span className="text-sm text-textSecondary dark:text-[#C9A3FF]">
              Answer Breakdown
            </span>
            <div className={`flex h-2.5 rounded-full overflow-hidden ${isDarkMode === 'dark' ? 'bg-gray-700/40' : 'bg-gray-100'}`}>
              <div className="bg-[#34B53A]" style={{ width: `${pct(correctCount)}%` }} />
              <div className="bg-[#DC0000]" style={{ width: `${pct(incorrectCount)}%` }} />
              <div className={isDarkMode === 'dark' ? 'bg-gray-600' : 'bg-gray-300'} style={{ width: `${pct(skippedCount)}%` }} />
            </div>
            <div className="flex flex-wrap gap-x-4 gap-y-1.5 text-xs text-gray-500 dark:text-gray-400">
              <span className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-[#34B53A]" />
                {correctCount} Correct
              </span>
              <span className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-[#DC0000]" />
                {incorrectCount} Incorrect
              </span>
              <span className="flex items-center gap-1.5">
                <span className={`w-2 h-2 rounded-full ${isDarkMode === 'dark' ? 'bg-gray-600' : 'bg-gray-300'}`} />
                {skippedCount} Skipped
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between gap-3 px-1">
        <span className="text-xs text-gray-400 dark:text-gray-500 min-w-0 truncate">
          {formatReportDate(mcqTestResultData.startDateTime)
            ? `Attempted on ${formatReportDate(mcqTestResultData.startDateTime)}`
            : ''}
        </span>
      </div>

      {/* ── Questions & Answers ── */}
      {totalQuestions === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <ClipboardList className="w-10 h-10 text-gray-300 dark:text-gray-600 mb-3" />
          <p className="text-sm text-gray-500 dark:text-[#A8A8A8]">
            No MCQ questions found for this assessment.
          </p>
        </div>
      ) : (
        <>
          <div className="flex items-center">
            <ClipboardList className="w-5 h-5 text-gray-700 dark:text-[#C9A3FF] flex-shrink-0" />
            <span className="text-sm text-textSecondary dark:text-[#C9A3FF] ml-2">
              Questions &amp; Answers
            </span>
            <div className="ml-3 flex-1 border-t border-[#52525280]" />
          </div>

          <div className="space-y-3">
            {questions.map((questionData, index) => {
              const isAttempted =
                questionData.selected_option_id !== undefined &&
                questionData.selected_option_id !== null;
              const isQuestionCorrect =
                isAttempted && questionData.selected_option_id === questionData.correct_option_id;

              const statusPill = !isAttempted
                ? {
                    label: 'Not Attempted',
                    className: 'bg-gray-100 text-gray-500 border-[#52525280] dark:bg-gray-700/50 dark:text-[#A8A8A8]',
                  }
                : isQuestionCorrect
                ? {
                    label: 'Correct',
                    className: 'bg-[#34B53A1A] border-[#34B53A66] text-green-600 dark:text-[#34B53A]',
                  }
                : {
                    label: 'Incorrect',
                    className: 'bg-[#DC00001A] border-[#DC000066] text-red-600 dark:text-red-400',
                  };

              return (
                <div
                  key={questionData.id ?? index}
                  className="rounded-2xl border border-[#52525280] overflow-hidden"
                >
                  <div className="flex items-center gap-3 px-5 py-3">
                    <pre className="flex-1 min-w-0 text-sm font-sans font-semibold text-gray-900 dark:text-white whitespace-pre-wrap break-words m-0">
                      <span className="text-gray-900 dark:text-white">Q{index + 1}.</span>{' '}
                      {questionData.text}
                    </pre>
                    <span
                      className={`flex-shrink-0 text-sm px-3 py-1 rounded-full border font-medium ${statusPill.className}`}
                    >
                      {statusPill.label}
                    </span>
                  </div>

                  <div className="border-t border-[#52525280] px-5 py-3 space-y-2">
                    {(questionData.options || []).map((option, optIndex) => {
                      // Matched by option id, so a reworded option still resolves.
                      const isCorrect = option.id === questionData.correct_option_id;
                      const isGivenAnswer = option.id === questionData.selected_option_id;

                      
                      const optionClass = isCorrect
                        ? 'bg-[#34B53A1A] text-green-700 dark:text-[#34B53A]'
                        : isGivenAnswer
                        ? 'bg-[#DC00001A] text-red-600 dark:text-red-400'
                        : 'bg-gray-100 text-gray-700 dark:bg-white/[0.06] dark:text-[#A8A8A8]';

                      const badgeClass = isCorrect
                        ? 'bg-[#34B53A33]'
                        : isGivenAnswer
                        ? 'bg-[#DC000033]'
                        : 'bg-gray-200 dark:bg-white/10';

                      return (
                        <div
                          key={option.id ?? optIndex}
                          className={`flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm ${optionClass}`}
                        >
                          <span className={`flex-shrink-0 w-6 h-6 rounded-md text-xs font-semibold flex items-center justify-center ${badgeClass}`}>
                            {String.fromCharCode(65 + optIndex)}
                          </span>
                          <span className="flex-1 min-w-0 break-words">{option.text}</span>
                          {isCorrect ? (
                            <CircleCheck className="w-4 h-4 flex-shrink-0" />
                          ) : isGivenAnswer ? (
                            <CircleX className="w-4 h-4 flex-shrink-0" />
                          ) : null}
                          {(isGivenAnswer || isCorrect) && (
                            <span className="flex-shrink-0 text-xs font-medium">
                              {isGivenAnswer ? 'Your answer' : 'Correct answer'}
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
